import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api } from "@/lib/api";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", gdpr_accepted: false });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_accepted) {
      toast.error("Kérlek fogadd el az adatkezelési tájékoztatót");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Üzenetedet megkaptuk! Hamarosan jelentkezünk.");
      setForm({ name: "", email: "", phone: "", message: "", gdpr_accepted: false });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Hiba az üzenet küldésekor";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kapcsolat"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#FAF9F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left – info + map */}
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">Kapcsolat</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
              Beszéljünk<br /><span className="italic">személyesen.</span>
            </h2>

            <ul className="mt-12 space-y-8" data-testid="contact-info">
              <li className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-[#BFA588] mt-1" />
                <div>
                  <p className="eyebrow mb-1">E-mail</p>
                  <a href="mailto:info@villaporcelaine.com" className="text-lg text-[#1A1A1A] hover:text-[#BFA588] transition-colors" data-testid="contact-email">
                    info@villaporcelaine.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-[#BFA588] mt-1" />
                <div>
                  <p className="eyebrow mb-1">Telefon</p>
                  <a href="tel:+36304549308" className="text-lg text-[#1A1A1A] hover:text-[#BFA588] transition-colors" data-testid="contact-phone">
                    +36 30 454 9308
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#BFA588] mt-1" />
                <div>
                  <p className="eyebrow mb-1">Cím</p>
                  <p className="text-lg text-[#1A1A1A]" data-testid="contact-address">
                    Villa Porcelaine<br />
                    Erzsébet királyné útja 38.<br />
                    8360 Keszthely
                  </p>
                </div>
              </li>
            </ul>

            <div className="mt-12 border border-[#E5E0D8] overflow-hidden" data-testid="map-embed">
              <iframe
                title="Villa Porcelaine helyszín"
                src="https://www.google.com/maps?q=Keszthely+Erzs%C3%A9bet+kir%C3%A1lyn%C3%A9+%C3%BAtja+38&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right – form */}
          <div className="lg:col-span-6 lg:pt-20">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-[#E5E0D8] p-8 md:p-12"
            >
              <h3 className="font-serif-display text-3xl md:text-4xl mb-2">Írj nekünk</h3>
              <p className="text-[#595959] text-sm mb-10">Válaszolunk 24 órán belül.</p>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="c-name" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Név *</Label>
                  <Input
                    id="c-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">E-mail *</Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="c-phone" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Telefonszám *</Label>
                  <Input
                    id="c-phone"
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="c-msg" className="text-[12px] tracking-[0.18em] uppercase text-[#595959]">Üzenet *</Label>
                  <Textarea
                    id="c-msg"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    required
                    className="mt-2 rounded-none border-0 border-b border-[#E5E0D8] focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 text-base"
                  />
                </div>
              </div>

              <div className="mt-8 flex items-start gap-3">
                <Checkbox
                  id="c-gdpr"
                  data-testid="contact-gdpr"
                  checked={form.gdpr_accepted}
                  onCheckedChange={(v) => setForm({ ...form, gdpr_accepted: !!v })}
                  className="mt-1 rounded-none"
                />
                <label htmlFor="c-gdpr" className="text-sm text-[#595959] leading-relaxed">
                  Elfogadom az adatkezelési tájékoztatót, és hozzájárulok ahhoz, hogy adataimat a kapcsolatfelvétel céljából kezeljék. *
                </label>
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="btn-primary mt-8 w-full disabled:opacity-40"
              >
                {submitting ? "Küldés…" : "Üzenet küldése"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
